import { useState } from "react";
import { Plus, Trash2, Tv, Smartphone, Monitor, Cast, ShieldCheck } from "lucide-react";
import Nav from "@/saas/components/Nav";
import Footer from "@/saas/components/Footer";
import SButton from "@/saas/components/SButton";
import { Field, Input } from "@/saas/components/AuthShell";
import { useI18n } from "@/saas/i18n";
import { useSaasAuth } from "@/saas/context/SaasAuthContext";

// Mock devices (Phase 1). Replace with API in Phase 2.
const INITIAL = [
  { id: "d1", name: "Living Room TV", platform: "samsung_tv", added: "2026-02-05T10:00:00Z", last_seen: new Date(Date.now() - 1000*60*12).toISOString() },
  { id: "d2", name: "iPhone 15",      platform: "ios",        added: "2026-02-10T12:00:00Z", last_seen: new Date(Date.now() - 1000*60*60*3).toISOString() },
];

const PLATFORM_ICON = {
  ios:        Smartphone,
  android:    Smartphone,
  android_tv: Tv,
  apple_tv:   Tv,
  fire_tv:    Tv,
  samsung_tv: Tv,
  lg_tv:      Tv,
  windows:    Monitor,
  mac:        Monitor,
  web:        Cast,
};

export default function Devices() {
  const { t, locale } = useI18n();
  const { user } = useSaasAuth();
  const [devices, setDevices] = useState(INITIAL);
  const [addOpen, setAddOpen] = useState(false);
  const [revokeDev, setRevokeDev] = useState(null);
  const [code, setCode] = useState("");

  const max = user?.devicesMax ?? 3;

  const submitAdd = (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return;
    setDevices((d) => [
      ...d,
      { id: `d${Date.now()}`, name: `Device ${d.length + 1}`, platform: "web", added: new Date().toISOString(), last_seen: new Date().toISOString() },
    ]);
    setCode("");
    setAddOpen(false);
  };

  const confirmRevoke = () => {
    setDevices((d) => d.filter((x) => x.id !== revokeDev.id));
    setRevokeDev(null);
  };

  return (
    <div className="min-h-screen bg-bg text-white">
      <Nav />
      <main className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
            <div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight leading-tight mb-2">
                {t("devices.title")}
              </h1>
              <p className="text-white/60">{t("devices.subtitle")}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{t("dash.devices.title")}</div>
                <div className="font-display text-2xl font-bold">{devices.length}<span className="text-white/30"> / {max}</span></div>
              </div>
              <SButton icon={Plus} onClick={() => setAddOpen(true)} disabled={devices.length >= max} data-testid="devices-add-btn">
                {t("devices.add")}
              </SButton>
            </div>
          </div>

          {devices.length === 0 ? (
            <div className="p-10 rounded-2xl border border-dashed border-white/15 bg-surface/40 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gold/10 border border-gold/30 grid place-items-center">
                <Smartphone className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">{t("devices.empty.title")}</h3>
              <p className="text-white/60 max-w-sm mx-auto mb-6">{t("devices.empty.subtitle")}</p>
              <SButton icon={Plus} onClick={() => setAddOpen(true)}>{t("devices.add")}</SButton>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-surface/60 overflow-hidden">
              <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-4 px-5 py-3 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/40">
                <div>{t("devices.name")}</div>
                <div>{t("devices.platform")}</div>
                <div>{t("devices.added")}</div>
                <div>{t("devices.last_seen")}</div>
                <div />
              </div>
              {devices.map((d) => {
                const Icon = PLATFORM_ICON[d.platform] || Cast;
                return (
                  <div
                    key={d.id}
                    className="grid md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-4 px-5 py-4 border-b border-white/5 last:border-0 items-center hover:bg-white/[0.02] transition"
                    data-testid={`device-row-${d.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 grid place-items-center shrink-0">
                        <Icon className="w-4 h-4 text-white/70" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{d.name}</div>
                        <div className="text-[11px] font-mono text-white/40 flex items-center gap-1.5">
                          <ShieldCheck className="w-3 h-3 text-ok" /> secure
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-white/80">{t(`app.platform.${d.platform}`)}</div>
                    <div className="text-sm text-white/60 font-mono">{new Date(d.added).toLocaleDateString(locale)}</div>
                    <div className="text-sm text-white/60 font-mono">{new Date(d.last_seen).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" })}</div>
                    <div className="justify-self-end">
                      <button
                        onClick={() => setRevokeDev(d)}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-err/10 border border-err/30 text-err hover:bg-err/20 text-xs font-semibold"
                        data-testid={`device-revoke-${d.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> {t("devices.revoke")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Add modal */}
      {addOpen && (
        <Backdrop onClose={() => setAddOpen(false)}>
          <form onSubmit={submitAdd} className="w-full max-w-md">
            <h3 className="font-display text-2xl font-bold mb-2">{t("devices.add.title")}</h3>
            <p className="text-sm text-white/60 mb-6">{t("devices.add.desc")}</p>
            <Field label={t("devices.add.code")} hint={t("devices.add.hint")}>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                className="tracking-[0.5em] text-center text-2xl font-mono"
                autoFocus
                data-testid="devices-add-code"
              />
            </Field>
            <div className="flex gap-3 mt-6">
              <SButton type="button" variant="outline" className="flex-1" onClick={() => setAddOpen(false)}>
                {t("common.cancel")}
              </SButton>
              <SButton type="submit" className="flex-1" disabled={!/^\d{6}$/.test(code)} data-testid="devices-add-submit">
                {t("devices.add.submit")}
              </SButton>
            </div>
          </form>
        </Backdrop>
      )}

      {/* Revoke modal */}
      {revokeDev && (
        <Backdrop onClose={() => setRevokeDev(null)}>
          <div className="w-full max-w-md">
            <h3 className="font-display text-2xl font-bold mb-2">{t("devices.revoke.title")}</h3>
            <p className="text-sm text-white/60 mb-6">
              {t("devices.revoke.desc", { name: revokeDev.name })}
            </p>
            <div className="flex gap-3">
              <SButton variant="outline" className="flex-1" onClick={() => setRevokeDev(null)}>
                {t("devices.revoke.cancel")}
              </SButton>
              <SButton variant="danger" className="flex-1" onClick={confirmRevoke} data-testid="devices-revoke-confirm">
                {t("devices.revoke.confirm")}
              </SButton>
            </div>
          </div>
        </Backdrop>
      )}

      <Footer />
    </div>
  );
}

function Backdrop({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm grid place-items-center p-6 animate-fade-up"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-white/10 rounded-2xl p-8 shadow-2xl"
      >
        {children}
      </div>
    </div>
  );
}
